<template>
  <NamiLoading />
</template>

<script setup lang="ts">
import { Title } from "~/src/api";

const nuxtApp = useNuxtApp();
const authStore = nuxtApp.$auth();
const settingsStore = nuxtApp.$settings();
const route = useRoute();
const { selectLocalizedCover } = useMediaLink();

const { locale } = useI18n();
const config = useRuntimeConfig();

const { data: title } = useAsyncData(route.params.id as string, async () => {
  const data = await Title.get(route.params.id as string, [
    "cover_art",
    "organization",
    "tag",
  ]);

  return data;
});

const seo = computed(() =>
  title.value
    ? {
        title: extractFromLocale(locale.value, title.value.attributes.title),
        description: extractFromLocale(
          locale.value,
          title.value.attributes.description,
        ),
        slug: title.value.attributes.slug,
        coverUrl: selectLocalizedCover(locale.value, title.value, "512"),
      }
    : {},
);

useServerSeoMeta({
  title: () => seo.value.title ?? "",
  ogTitle: () => seo.value.title,
  twitterTitle: () => seo.value.title,
  description: () => seo.value.description,
  ogDescription: () => seo.value.description,
  twitterDescription: () => seo.value.description,
  ogUrl: () => (title.value ? linkTo(title.value) : ""),
  ogLocale: () => locale.value,
  ogImage: () => seo.value.coverUrl,
  ogImageSecureUrl: () => seo.value.coverUrl,
  twitterImage: () => seo.value.coverUrl,
  ogImageHeight: "1200",
  ogImageWidth: "630",
  ogImageUrl: () => seo.value.coverUrl,
  ogImageType: "image/png",
  ogImageAlt: "NamiComi",
  ogLocaleAlternate: "en_GB",
});

useHead({
  link: () => [
    {
      rel: "canonical",
      href: `${config.public.baseUrl}/en/title/${route.params.id}/${seo.value.slug}`,
    },
  ],
});

onMounted(() => {
  watch(
    title,
    async (v) => {
      if (!v) {
        return;
      }

      await authStore?.waitUntilRefreshIsComplete();
      await settingsStore?.waitForInitialization();

      return navigateTo(linkTo(v), { replace: true });
    },
    { immediate: true },
  );
});
</script>
